import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Dispatch, SetStateAction, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { checkUser, loginWithEmail, resetPassword } from "@/services/auth";
import Loading from "@/components/ui/loading";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/dialog";
import Link from "next/link";

export default function LoginSection() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetLoading, setResetLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim() || !password.trim()) {
      setError("Email and password are required");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      await loginWithEmail(email, password);
      router.replace("/dashboard");
    } catch (err: any) {
      if (err.message == "Firebase: Error (auth/invalid-credential).") {
        setError("Invalid Credential, Please try again. ");
      } else {
        setError("Login failed, Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    setResetLoading(true);
    const exists = await checkUser(resetEmail);
    setResetError(null);
    setResetMessage(null);

    if (!exists) {
      setResetError("No account found with this email.");
      setResetLoading(false);
      return;
    }

    await resetPassword(resetEmail);
    setResetMessage(
      "Password reset email sent! Please check your inbox, and if you don't see it, check your Spam or Junk folder."
    );
    setResetLoading(false);
  };

  return (
    <main className="overflow-hidden h-screen flex justify-center items-center p-6">
      {loading ? <Loading /> : null}
      <Link href={"/"} className="absolute top-7">
        <Image
          src={"/assets/logo.png"}
          alt=""
          width={30}
          height={30}
          className="opacity-75 "
        />
      </Link>
      <form
        onSubmit={onSubmit}
        className="w-full flex justify-center items-center"
      >
        <Card className="w-full sm:max-w-md bg-zinc-800/30 border-none backdrop-blur-sm shadow-2xl text-gray-300">
          <CardHeader>
            <CardTitle className="text-xl">Login to your account</CardTitle>
            <CardDescription>
              Enter your email below to login to your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-6">
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  className="px-2"
                  id="email"
                  type="email"
                  placeholder="johndoe@example.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <div className="flex items-center">
                  <Label htmlFor="password">Password</Label>

                  <Dialog>
                    <DialogTrigger asChild>
                      <button
                        type="button"
                        className="ml-auto text-sm italic text-gray-600 hover:text-gray-400 underline-offset-4 hover:underline"
                      >
                        Forgot your password?
                      </button>
                    </DialogTrigger>
                    <DialogContent className="bg-zinc-800/90 text-gray-200 border-none shadow-xl w-full max-w-[90%] sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Reset Password</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="grid gap-2">
                          <Input
                            className="px-2"
                            id="resetEmail"
                            type="email"
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            placeholder="johndoe@example.com"
                          />
                        </div>
                        {resetError && (
                          <p className="text-red-500 italic px-1">
                            {resetError}
                          </p>
                        )}
                        {resetMessage && (
                          <p className="text-green-500 italic px-1 text-sm">
                            {resetMessage}
                          </p>
                        )}
                        <Button
                          onClick={handleReset}
                          disabled={resetLoading}
                          className="w-full bg-zinc-400 hover:bg-zinc-200 text-gray-800"
                        >
                          {resetLoading ? "Loading..." : "Send Reset Email"}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <Input
                  className="px-2"
                  id="password"
                  type="password"
                  required
                  placeholder="* * * * * * * *"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex-col gap-2 mt-2">
            {error && (
              <p className="text-red-500 italic px-1 mb-2 -mt-4 text-start w-full">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-zinc-400 hover:bg-zinc-200 text-gray-800 hover:text-gray-900"
            >
              {loading ? "Loading..." : "Login"}
            </Button>
            <Link
              href={"/register"}
              className="w-fit text-gray-400 hover:text-gray-100"
            >
              Don&apos;t have an account? Create account
            </Link>
          </CardFooter>
        </Card>
      </form>
    </main>
  );
}
